import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { toast as sonnerToast } from 'sonner';

import {
  CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
  chronicleInterruptedSessionAtom,
  chronicleIsRecordingAtom,
  chronicleLiveOfflineAtom,
  chronicleLowBatteryAtom,
} from '@/atoms/chronicle';
import {
  usbAccessDeniedAtom,
  usbAccessDeniedPromptAtom,
} from '@/atoms/chronicle/bulk-sync';
import { trackHardwareLowBatteryShown } from '@/utils/analytics/track-hardware-events';
import { logger } from '@/utils/logger/logger';
import { Link } from '@heidi/design-system/link';
import { Toast, toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';

const TOAST_POSITION = 'bottom-right';

// A live-offline hold starts on any drop mid-recording; a self-healing flap
// clears it within a repark cycle (~10s) without the recording ever being at
// risk. Hold the persistent toast back this long so a blip that recovers stays
// silent, while a genuine sustained hold (up to the 120s finalize) still
// surfaces it well before it converts to an interruption.
const LIVE_OFFLINE_HOLD_TOAST_DEBOUNCE_MS = 10_000;

const poweroffMessages = defineMessages({
  title: {
    id: 'KbnILuSjoi',
    defaultMessage: 'Heidi Remote shut down — battery empty',
    description: 'Toast title when Remote powers off due to low battery',
  },
  description: {
    id: 'XYfI4WxTmw',
    defaultMessage:
      'Charge your Heidi Remote and power it back on. Any recording on the device will sync when it reconnects.',
    description: 'Toast body for low-battery shutdown',
  },
});

/**
 * Hook for managing Chronicle device notifications (toasts).
 * Monitors device state changes and shows appropriate notifications.
 *
 * Notifications:
 * - Unexpected disconnect (not recording): "Chronicle is disconnected"
 * - Unexpected disconnect (during recording): persistent "Chronicle connection was interrupted"
 * - Low-battery power-off: "Remote shut down — battery empty"
 * - Max recording duration (Remote 5 h cap): same as in-app recorder toast
 * - Reconnect success: "Chronicle reconnected"
 * - Error during recording: "Chronicle encountered an error" (FR-013)
 * - Low battery (≤10%): "Chronicle has low battery" (persistent, auto-dismisses when resolved)
 *
 * Mount this hook in a component that's always rendered when Chronicle features are active.
 */
export function useChronicleNotifications() {
  const { t } = useTranslations();
  const intl = useIntl();
  const connectionContext = useAtomValue(chronicleConnectionContextAtom);
  const deviceState = useAtomValue(chronicleDeviceStateAtom);
  const isRecording = useAtomValue(chronicleIsRecordingAtom);
  const isLowBattery = useAtomValue(chronicleLowBatteryAtom);
  const interruptedSession = useAtomValue(chronicleInterruptedSessionAtom);
  const setInterruptedSession = useSetAtom(chronicleInterruptedSessionAtom);
  const liveOffline = useAtomValue(chronicleLiveOfflineAtom);
  const usbAccessDenied = useAtomValue(usbAccessDeniedAtom);
  const setUsbAccessDeniedPrompt = useSetAtom(usbAccessDeniedPromptAtom);
  const prevDisconnectReasonRef = useRef(connectionContext.lastDisconnectReason);
  const prevConnectionStatusRef = useRef(deviceState.connectionStatus);
  const prevIsLowBatteryRef = useRef(isLowBattery);
  // Initialise to the current atom value so a hook re-mount (e.g. HMR or
  // route remount) with an already-populated atom does not re-fire the toast.
  const lastUsbAccessDeniedRef = useRef(usbAccessDenied);
  const prevLastStopReasonRef = useRef(deviceState.lastStopReason);
  const lowBatteryToastIdRef = useRef<string | number | null>(null);
  const interruptedToastIdRef = useRef<string | number | null>(null);
  const holdToastIdRef = useRef<string | number | null>(null);
  const holdToastDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Remote 5 h recorded-audio cap (HAR-1311) — same copy/tone as the in-app
  // recorder. Ungated (Tom option 1): the device has already stopped by the
  // time this runs; the toast only explains a stop that happened — nothing to
  // kill-switch. Fires once when `lastStopReason` transitions to `max_duration`
  // (connected MAXDUR→STO, or reconnect latch). Sticky Rust reason + FE
  // prev-ref make reconnect re-sends idempotent within a single app session
  // (in-memory `last_stop_reason`; a process restart may toast again — accepted).
  // Telemetry lives in `useChronicleStopRecording` (single HardwareRecordingStop
  // with duration/session ids) — do not emit a second stop event here.
  // Depend on whole `deviceState` object identity — same sticky reason, new
  // object (Rust rebroadcast shape) — so the effect re-runs and the prev-ref
  // can suppress a second toast. Do not narrow to `deviceState.lastStopReason`:
  // that keeps the suite green while making the re-run guard unobservable
  // (effect simply stops firing). Battery/RSSI broadcasts no-op cheaply here.
  useEffect(() => {
    const prevReason = prevLastStopReasonRef.current;
    const currentReason = deviceState.lastStopReason;
    prevLastStopReasonRef.current = currentReason;

    if (currentReason === 'max_duration' && prevReason !== 'max_duration') {
      toast({
        title: t('sessions.recordingStopped'),
        description: t('sessions.maxRecordingTimeReached'),
        tone: 'destructive',
        variant: 'outline',
        duration: 10000,
        position: TOAST_POSITION,
      });
    }
  }, [deviceState, t]);

  // Show toast when unexpected disconnect / batlow power-off occurs or when reconnect succeeds
  useEffect(() => {
    const prevReason = prevDisconnectReasonRef.current;
    const currentReason = connectionContext.lastDisconnectReason;

    // Deliberate low-battery shutdown — distinct from a link failure.
    if (currentReason === 'poweroff_batlow' && prevReason !== 'poweroff_batlow') {
      toast({
        title: intl.formatMessage(poweroffMessages.title),
        description: intl.formatMessage(poweroffMessages.description),
        tone: 'warning',
        variant: 'outline',
        duration: 10000,
        position: TOAST_POSITION,
      });
    }

    // Show toast when transitioning TO 'unexpected' (disconnect detected)
    if (currentReason === 'unexpected' && prevReason !== 'unexpected') {
      if (isRecording) {
        logger.warn(
          '[useChronicleNotifications] Chronicle disconnected during recording — persistent toast will be shown'
        );
      } else {
        // Idle disconnect — standard reconnect guidance
        toast({
          title: t('settings.chronicle.notification.disconnectedTitle'),
          description: (
            <>
              {t('settings.chronicle.notification.disconnectedDescription')}{' '}
              <Link href="/devices" variant="inline">
                {t('settings.chronicle.notification.stillNotReconnecting')}
              </Link>
            </>
          ),
          tone: 'warning',
          variant: 'outline',
          duration: 8000,
          position: TOAST_POSITION,
        });
      }
    }

    // Show toast when transitioning FROM 'unexpected' to null (reconnect success)
    if (currentReason === null && prevReason === 'unexpected') {
      toast({
        title: t('settings.chronicle.notification.reconnectedTitle'),
        description: t('settings.chronicle.notification.reconnectedDescription'),
        tone: 'success',
        variant: 'outline',
        duration: 5000,
        position: TOAST_POSITION,
      });
    }

    prevDisconnectReasonRef.current = currentReason;
  }, [connectionContext.lastDisconnectReason, isRecording, t, intl]);

  // Persistent interruption toast — driven by chronicleInterruptedSessionAtom.
  // APP-7867: suppressed while live-offline is held (APP-7866) — the atom is
  // stale by construction during that window, but the guard is explicit here
  // so the toast never surfaces one anyway. Not sticky: re-evaluated on the
  // current atom values, so it resumes normally once live-offline clears.
  useEffect(() => {
    if (interruptedSession && !liveOffline && interruptedToastIdRef.current === null) {
      const toastId = sonnerToast.custom(
        (id) => (
          <Toast
            title={t('settings.chronicle.notification.interruptedTitle')}
            description={t('settings.chronicle.notification.interruptedDescription')}
            tone="warning"
            variant="outline"
            onDismiss={() => {
              sonnerToast.dismiss(id);
              interruptedToastIdRef.current = null;
              setInterruptedSession(null);
            }}
          />
        ),
        { duration: Infinity, position: TOAST_POSITION }
      );
      interruptedToastIdRef.current = toastId;
    } else if (
      (!interruptedSession || liveOffline) &&
      interruptedToastIdRef.current !== null
    ) {
      sonnerToast.dismiss(interruptedToastIdRef.current);
      interruptedToastIdRef.current = null;
    }
  }, [interruptedSession, liveOffline, setInterruptedSession, t]);

  // Persistent toast while a live-offline hold waits for a reconnect. The
  // interruption toast above is deliberately suppressed during the hold, which
  // previously left the frozen recording UI with no explanation at all. Reuses
  // the disconnect copy (locale JSON is pipeline-owned; bespoke hold copy can
  // follow through it) and auto-dismisses the moment the hold releases —
  // same lifecycle as the low-battery toast below.
  //
  // Debounced (HAR-1860): the toast only surfaces if the hold is still active
  // after LIVE_OFFLINE_HOLD_TOAST_DEBOUNCE_MS, so a flap that self-heals inside
  // that window never flashes it.
  useEffect(() => {
    if (liveOffline) {
      if (holdToastIdRef.current === null && holdToastDebounceTimerRef.current === null) {
        holdToastDebounceTimerRef.current = setTimeout(() => {
          holdToastDebounceTimerRef.current = null;
          holdToastIdRef.current = toast({
            title: t('settings.chronicle.notification.disconnectedTitle'),
            description: (
              <>
                {t('settings.chronicle.notification.disconnectedDescription')}{' '}
                <Link href="/devices" variant="inline">
                  {t('settings.chronicle.notification.stillNotReconnecting')}
                </Link>
              </>
            ),
            tone: 'warning',
            variant: 'outline',
            duration: Infinity,
            position: TOAST_POSITION,
          });
        }, LIVE_OFFLINE_HOLD_TOAST_DEBOUNCE_MS);
      }
    } else {
      if (holdToastDebounceTimerRef.current !== null) {
        clearTimeout(holdToastDebounceTimerRef.current);
        holdToastDebounceTimerRef.current = null;
      }
      if (holdToastIdRef.current !== null) {
        sonnerToast.dismiss(holdToastIdRef.current);
        holdToastIdRef.current = null;
      }
    }
  }, [liveOffline, t]);

  // Clear any pending hold-toast debounce on unmount so a fired timer never
  // touches a dismissed hook.
  useEffect(
    () => () => {
      if (holdToastDebounceTimerRef.current !== null) {
        clearTimeout(holdToastDebounceTimerRef.current);
        holdToastDebounceTimerRef.current = null;
      }
    },
    []
  );

  // T037: Show error toast when device transitions to 'error' during recording
  useEffect(() => {
    const prevStatus = prevConnectionStatusRef.current;
    const currentStatus = deviceState.connectionStatus;
    prevConnectionStatusRef.current = currentStatus;

    if (
      currentStatus === CONNECTION_STATUS.ERROR &&
      prevStatus !== CONNECTION_STATUS.ERROR &&
      isRecording
    ) {
      logger.warn('[useChronicleNotifications] Chronicle error during recording', {
        error: deviceState.error,
      });
      toast({
        title: t('settings.chronicle.notification.errorDuringRecordingTitle'),
        description:
          deviceState.error?.message ??
          t('settings.chronicle.notification.errorDuringRecordingDescription'),
        tone: 'destructive',
        variant: 'outline',
        duration: 8000,
        position: TOAST_POSITION,
      });
    }
  }, [deviceState.connectionStatus, deviceState.error, isRecording, t]);

  // Show persistent toast when battery drops to ≤10%, auto-dismiss when resolved
  useEffect(() => {
    const wasLowBattery = prevIsLowBatteryRef.current;
    prevIsLowBatteryRef.current = isLowBattery;

    if (isLowBattery && !wasLowBattery) {
      const id = toast({
        title: t('settings.chronicle.lowBattery.title'),
        description: t('settings.chronicle.lowBattery.description', {
          batteryLevel: deviceState.batteryLevel,
        }),
        tone: 'warning',
        variant: 'outline',
        duration: Infinity,
        position: TOAST_POSITION,
      });
      lowBatteryToastIdRef.current = id;
      trackHardwareLowBatteryShown(deviceState.batteryLevel ?? 0);
    }

    if (!isLowBattery && wasLowBattery && lowBatteryToastIdRef.current !== null) {
      sonnerToast.dismiss(lowBatteryToastIdRef.current);
      lowBatteryToastIdRef.current = null;
    }
  }, [isLowBattery, deviceState.batteryLevel, t]);

  // APP-4668: OS refused access to a removable drive (Windows: corp GPO / EDR
  // / BitLocker; macOS: TCC denial for Removable Volumes). Show a warning
  // modal on every fresh denial event. Rust already dedupes — each
  // `chronicle:usb_access_denied` payload represents a *new* detection of a
  // denied mount — so we can fire on every identity change instead of relying
  // on a `null → set` round-trip via the unmount handler. The previous
  // `prev === null` gate broke when the unmount event was missed (FSEvents
  // delivering Remove on a child path, or starvation of the periodic poll on
  // macOS), suppressing the prompt on the second and subsequent plug-ins.
  useEffect(() => {
    if (usbAccessDenied === null) {
      lastUsbAccessDeniedRef.current = null;
      return;
    }
    // APP-6883: only surface the Remote USB-C sync failure to users who
    // actually have a paired Heidi Remote. The Rust USB watcher runs for every
    // desktop user and — on Windows, pending the precise volume-label gate
    // (APP-4677) — emits `usb_access_denied` for *any* access-denied removable
    // drive, not just a confirmed HeidiRemote volume. Without this gate a
    // non-Remote user on a locked-down corporate machine (GPO/EDR blocking USB
    // storage) sees a confusing "USB-C sync failed" modal triggered by
    // unrelated USB storage. We keep the upstream `hardware_usb_access_denied`
    // telemetry flowing — only the user-facing modal is gated.
    //
    // Note: we return *before* advancing the ref so the modal still fires if
    // the linked device is restored from persistence after the denial event
    // arrives (cold-start race) — hence `linkedDeviceId` is in the deps below.
    if (connectionContext.linkedDeviceId === null) {
      return;
    }
    if (lastUsbAccessDeniedRef.current === usbAccessDenied) {
      return;
    }
    lastUsbAccessDeniedRef.current = usbAccessDenied;
    logger.warn('[useChronicleNotifications] USB access denied — showing failure modal');
    setUsbAccessDeniedPrompt(usbAccessDenied);
  }, [setUsbAccessDeniedPrompt, usbAccessDenied, connectionContext.linkedDeviceId]);
}
